import {
  MAX_SCHEDULED_MESSAGES_PER_DAY,
  MAX_SCHEDULED_MESSAGES_SENT_PER_DAY,
} from '@act/shared/constants';
import {
  MAX_TEXT_MESSAGE_LENGTH,
  MessageDto,
  MessageForm,
  MessageFormSchema,
  MustacheTemplateVariablesDto,
  SUPPORTED_SMS_FILE_SIZE,
  SUPPORTED_SMS_FILE_TYPES,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import SmsIcon from '@mui/icons-material/Sms';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { ClockIcon } from '@phosphor-icons/react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useToast } from 'react-components/app/ReusableToast';
import { TrackingProvider, useTracking } from 'react-components/tracking';
import { useForm } from 'react-hook-form';
import {
  createMessageSuccess,
  updateMessageSuccess,
} from 'redux/slices/messagesSlice';
import { messagesRequests } from 'redux/requests/messages.requests';
import { loadFile, selectFilesMap } from 'redux/slices/filesSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { useFeatureFlag } from 'redux/utils/feature-flags/feature-flag-hook';
import { uploadFiles } from 'redux/utils/file-uploader';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import styled from 'styled-components';
import StyledAlert from 'toolkit/alerts/styled-alert';
import { FileInput } from 'toolkit/media/file-input';
import FilePreviewer from 'toolkit/media/file-previewer/file-previewer';
import { FileThumbnail } from 'toolkit/media/file-thumbnail';
import { visitDateTimeHelpers } from 'utilities/date-time-generator';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import { applyMustacheTemplateWithValidation } from 'utilities/formatters/mustacheTemplateFormatter';
import CompositeDialog from '../../dialog/compositeDialog';
import MessageTemplates from '../components/messages/message-templates';
import useConfirmationAlertDialog from '../hooks/use-confirmation-alert-dialog';
import useUnsavedChangesDialog from '../hooks/use-unsaved-changes-dialog';
import './attachments.scss';
import ScheduleMessageDialog from './components/ScheduleMessageDialog';

const ButtonContainer = styled.div`
  padding-top: 8px;
  text-align: right;
`;

export interface MessageDialogInterfaceProps {
  user: UserDto;
  message?: MessageDto;
  scheduledEventId?: number;
  phoneNumber?: string;
}

export type MessageDialogProps = GenericDialogProps<boolean> &
  MessageDialogInterfaceProps;

interface MessageDialogPropsWithHandleClose
  extends MessageDialogInterfaceProps {
  handleClose?: () => void;
  mode?: 'Create' | 'Edit';
  open: boolean;
  setOpen?: (open: boolean) => void;
  callback?: (success: boolean) => void;
}

const MessageDialog = ({
  user,
  message,
  scheduledEventId = null,
  phoneNumber,
  open,
  setOpen,
  callback,
  handleClose,
}: MessageDialogPropsWithHandleClose) => {
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();
  const scheduledSmsFF = useFeatureFlag('scheduled-sms');
  const { generateTrackingProps } = useTracking();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filesToRemove, setFilesToRemove] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [formDataForScheduling, setFormDataForScheduling] = useState<any>(null);
  const [templateValidationError, setTemplateValidationError] = useState<
    string | null
  >(null);
  const [missingVariables, setMissingVariables] = useState<string[]>([]);
  const openConfirmationAlertDialog = useConfirmationAlertDialog();

  // Cleanup function to cancel ongoing uploads when component unmounts
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  const removeFile = (file: File) => {
    const allOtherFiles = selectedFiles.filter(f => f !== file);
    setSelectedFiles(allOtherFiles);
  };
  const [createdMessage, loadingCreateMessage, createMessage] = useApiCall(
    messagesRequests.createMessage
  );
  const [updatedMessage, loadingUpdateMessage, updateMessage] = useApiCall(
    messagesRequests.updateMessage
  );

  const { control, handleSubmit, setValue, getValues, formState, trigger } =
    useForm<MessageForm>({
      defaultValues: {
        phoneNumberId:
          message?.phoneNumberId ??
          user?.phoneNumbers?.find(phone => phone.primary)?.id ??
          -1,
        message: message?.text ?? '',
      },
      resolver: zodResolver(MessageFormSchema),
    });

  const removeExistingFile = (fileId: string) => {
    setFilesToRemove(prev => [...prev, fileId]);
  };

  const clearError = () => {
    setError(null);
  };

  const filesMap = useAppSelector(selectFilesMap);

  // Load existing files when editing a message
  useEffect(() => {
    if (message?.fileIds && message.fileIds.length > 0) {
      message.fileIds.forEach(fileId => {
        dispatch(loadFile({ id: fileId }));
      });
    }
  }, [message?.fileIds, dispatch]);

  function handleApplyTemplate(
    template: string,
    templateVariables: MustacheTemplateVariablesDto
  ) {
    const validationResult = applyMustacheTemplateWithValidation(
      template,
      templateVariables
    );

    setValue('message', validationResult.renderedTemplate, {
      shouldDirty: true,
    });

    if (validationResult.hasMissingVariables) {
      const errorMessage = `Some template variables are missing: ${validationResult.missingVariables.join(
        ', '
      )}. The message may appear incomplete.`;

      setTemplateValidationError(errorMessage);
      setMissingVariables(validationResult.missingVariables);
    } else {
      setTemplateValidationError(null);
      setMissingVariables([]);
    }
  }

  function clearMissingVariables() {
    setMissingVariables([]);
    setTemplateValidationError(null);
  }
  const { isValid } = formState;
  async function handleSchedule() {
    // Force form validation before opening the scheduling dialog

    if (!isValid) {
      // Form validation failed, don't open scheduling dialog
      trigger();
      return;
    }

    // Store current form data for scheduling
    setFormDataForScheduling(getValues());
    setIsScheduleDialogOpen(true);
  }

  function handleDialogClose(success = false) {
    // Call the parent's handleClose function if provided
    if (handleClose) {
      handleClose();
    }
    // If setOpen is provided, use it to close the dialog
    if (setOpen) {
      setOpen(false);
    }
    callback?.(success);
  }

  const phoneNumberOptions = useMemo(() => {
    const options = user?.phoneNumbers
      ?.filter(phone => phone?.type?.toLowerCase() !== 'landline')
      .map(phone => ({
        value: phone.id,
        label: `${phone.description}: ${phone.number}${
          phone.doNotContact ? ' (Do Not Contact)' : ''
        }`,
      }));
    if (options.length === 0) {
      return [{ value: -1, label: 'No phone numbers available to text.' }];
    }
    return options;
  }, [user?.phoneNumbers]);

  const hasNoPhoneNumbers =
    phoneNumberOptions.length === 1 && phoneNumberOptions[0].value === -1;

  useEffect(() => {
    if (hasNoPhoneNumbers && !message) {
      setValue('phoneNumberId', -1);
    }
  }, [hasNoPhoneNumbers, message, setValue]);

  const formConfig: FormConfig<MessageForm> = [
    {
      type: 'input',
      name: 'phoneNumberId',
      label: 'Phone Number',
      input: 'select',
      disabled: hasNoPhoneNumbers,
      trackingName: 'phone-number-select',
      props: {
        required: true,
        fullWidth: true,
        options: phoneNumberOptions || [],
      },
    },
    {
      type: 'input',
      name: 'message',
      label: 'Text Message',
      input: 'text',
      trackingName: 'message-text-input',
      props: {
        multiline: true,
        minRows: 4,
        characterLimit: MAX_TEXT_MESSAGE_LENGTH,
        placeholder: 'Enter your message...',
        onBlurCallback: () => clearMissingVariables(),
      },
    },
  ];
  function handleCancel() {
    if (isUploading && abortController) {
      // Cancel the upload but don't close the dialog
      abortController.abort();
      setIsUploading(false);
      setUploadProgress(0);
      setAbortController(null);
      return;
    }

    // If not uploading, close the dialog normally
    handleFormClose();
  }

  function handleScheduleMessageWrapper(
    data: MessageForm | { scheduleDate: Date; scheduleTime: number }
  ) {
    // If it's schedule data, call the original function
    if ('scheduleDate' in data && 'scheduleTime' in data) {
      void handleScheduleMessage(data);
    } else {
      // If it's form data, we need to handle it differently
      // This shouldn't happen in normal flow, but handle gracefully
      console.warn(
        'Received form data in schedule handler, this is unexpected'
      );
    }
  }

  async function handleScheduleMessage(scheduleData: {
    scheduleDate: Date;
    scheduleTime: number;
  }) {
    const scheduleDate = scheduleData.scheduleDate; // This could be a Date object or string
    const scheduleTimeMillis = scheduleData.scheduleTime; // This is milliseconds since start of day

    // Convert to DateTime - handle both Date objects and strings

    let sendAt = message?.sendAt;
    if (scheduleDate && scheduleTimeMillis) {
      // Create the scheduled datetime by combining the selected date with the time
      // The scheduleTimeMillis is already in milliseconds since start of day
      let selectedDate: DateTime;

      if (scheduleDate instanceof Date) {
        // Use the date as-is, treating it as local time
        selectedDate = DateTime.fromJSDate(scheduleDate);
      } else {
        selectedDate = DateTime.fromFormat(scheduleDate, 'yyyy-MM-dd', {
          zone: 'local',
        });
      }

      // Create the full datetime by adding the time offset to the start of the selected day
      const scheduledDateTime = selectedDate
        .startOf('day')
        .plus({ milliseconds: scheduleTimeMillis });

      // Convert to UTC milliseconds for storage
      sendAt = scheduledDateTime.toMillis();
    }

    // Get current form data - always use current form values, not original message data
    const formData = message
      ? getValues() // Use current form values when rescheduling
      : formDataForScheduling;

    let fileIds = [];

    // Start with existing files (excluding those marked for removal)
    if (message?.fileIds) {
      fileIds = message.fileIds.filter(
        fileId => !filesToRemove.includes(fileId)
      );
    }
    // Upload new files if any
    if (selectedFiles.length) {
      try {
        const files = await uploadFiles(selectedFiles);
        // Add new file IDs to existing ones
        fileIds = [...fileIds, ...files.map(f => f.id)];
      } catch (uploadError) {
        toast('Failed to upload files. Please try again.', 'error');
        handleDialogClose(false);
        return;
      }
    }

    const body = {
      subjectId: user.id,
      text: formData.message,
      fileIds,
      phoneNumberId: formData.phoneNumberId,
      scheduledEventId,
      sendAt,
    };

    try {
      if (message) {
        updateMessage({
          params: { messageId: message.id },
          body: {
            text: formData.message,
            fileIds,
            phoneNumberId: formData.phoneNumberId,
            sendAt,
          },
        });
      } else {
        createMessage({ params: null, body });
      }
    } catch (err) {
      // Handle specific scheduling limit errors
      const errorMessage =
        (err as any)?.message || (err as any)?.error?.message || '';
      if (
        errorMessage.includes(
          `Cannot schedule more than ${MAX_SCHEDULED_MESSAGES_PER_DAY} messages per day`
        )
      ) {
        toast(
          `Scheduling limit reached: You can only schedule ${MAX_SCHEDULED_MESSAGES_PER_DAY} messages per day. You can schedule ${MAX_SCHEDULED_MESSAGES_PER_DAY} messages each day.`,
          'error'
        );
      } else if (
        errorMessage.includes(
          `Cannot schedule more than ${MAX_SCHEDULED_MESSAGES_SENT_PER_DAY} messages to be sent`
        )
      ) {
        toast(
          `Daily scheduled message limit reached: You can only schedule ${MAX_SCHEDULED_MESSAGES_SENT_PER_DAY} messages to be sent per day. Manual messages are unlimited.`,
          'error'
        );
      } else {
        toast(
          message ? 'Failed to update message' : 'Failed to schedule message',
          'error'
        );
      }
      handleDialogClose(false);
    }
  }
  async function handleSave(data: MessageForm) {
    if (isSending || isUploading) return; // Prevent multiple submissions

    clearError(); // Clear any previous errors
    // Check for missing template variables using stored state
    if (missingVariables.length > 0) {
      const confirmed = await openConfirmationAlertDialog({
        title: 'Confirm Message Send',
        icon: <SmsIcon />,
        iconColor: 'warning',
        iconBorderColor: 'warningContainer',
        avatarCaption: 'Warning',
        message:
          'This message contains template variables that may not be filled in properly. The message may appear incomplete to the recipient.',
        cancelText: 'Cancel',
        confirmationText: 'Send Anyway',
        confirmButtonColor: 'warning',
      });

      if (!confirmed) {
        return; // User cancelled
      }
    }

    await proceedWithSave(data);
  }

  async function proceedWithSave(data: MessageForm) {
    let fileIds = [];

    // Start with existing files (excluding those marked for removal)
    if (message?.fileIds) {
      fileIds = message.fileIds.filter(
        fileId => !filesToRemove.includes(fileId)
      );
    }

    // Upload new files if any
    if (selectedFiles.length) {
      setIsUploading(true);
      setUploadProgress(0);

      // Create new abort controller for this upload
      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      try {
        const files = await uploadFiles(selectedFiles, {
          onProgress: progress => {
            setUploadProgress(progress);
          },
          abortController: newAbortController,
        });

        // Add new file IDs to existing ones
        fileIds = [...fileIds, ...files.map(f => f.id)];
      } catch (uploadError) {
        if (
          uploadError instanceof Error &&
          uploadError.message === 'Upload aborted'
        ) {
          // User cancelled the upload, don't show error
          setIsUploading(false);
          setUploadProgress(0);
          setAbortController(null);
          return;
        }

        setError('Failed to upload one or more files. Please try again.');
        setIsUploading(false);
        setAbortController(null);
        return;
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
        setAbortController(null);
      }
    }

    // Only set isSending to true after upload completes (or if no files to upload)
    setIsSending(true);

    const body = {
      subjectId: user.id,
      text: data.message,
      fileIds,
      phoneNumberId: data.phoneNumberId,
      scheduledEventId,
    };

    try {
      if (message) {
        // Update existing message
        updateMessage({
          params: { messageId: message.id },
          body: {
            text: data.message,
            fileIds,
            phoneNumberId: data.phoneNumberId,
          },
        });
      } else {
        // Create new message
        createMessage({ params: null, body });
      }
    } catch (err) {
      // Handle specific scheduling limit errors
      const errorMessage =
        (err as any)?.message || (err as any)?.error?.message || '';
      if (
        errorMessage.includes(
          `Cannot schedule more than ${MAX_SCHEDULED_MESSAGES_PER_DAY} messages per day`
        )
      ) {
        toast(
          `Scheduling limit reached: You can only schedule ${MAX_SCHEDULED_MESSAGES_PER_DAY} messages per day.`,
          'error'
        );
      } else if (
        errorMessage.includes(
          `Cannot schedule more than ${MAX_SCHEDULED_MESSAGES_SENT_PER_DAY} messages to be sent`
        )
      ) {
        toast(
          `Daily scheduled message limit reached: You can only schedule ${MAX_SCHEDULED_MESSAGES_SENT_PER_DAY} messages to be sent per day`,
          'error'
        );
      } else {
        setError(
          message ? 'Failed to update message' : 'Failed to send message'
        );
      }
      handleDialogClose(false);
    } finally {
      setIsSending(false);
    }
  }

  useEffect(() => {
    if (createdMessage) {
      dispatch(createMessageSuccess(createdMessage));
      !!createdMessage.sendAt
        ? toast('Message scheduled successfully', 'success')
        : toast('Message sent successfully', 'success');
      handleDialogClose(true);
    }
    if (updatedMessage) {
      dispatch(updateMessageSuccess(updatedMessage));
      !!updatedMessage.sendAt
        ? toast('Message scheduled successfully', 'success')
        : toast('Message sent successfully', 'success');
      handleDialogClose(true);
    }
  }, [createdMessage, updatedMessage]);

  const { isDirty } = formState;

  const handleFormClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        handleDialogClose();
      });
    } else {
      handleDialogClose();
    }
  };

  return (
    <TrackingProvider contextName="message-dialog">
      <CompositeDialog
        handleClose={handleFormClose}
        maxWidth="md"
        open={open}
        icon={<SmsIcon color="primary" sx={{ marginRight: 1 }} />}
        title={`${message ? 'Edit' : 'New'} Text Message for ${
          user?.firstName
        } ${user?.lastName}`}
        handleFormSubmit={handleSubmit(data => {
          if (message && message.sendAt) {
            void handleScheduleMessage({
              scheduleDate: new Date(message.sendAt),
              scheduleTime: visitDateTimeHelpers.getTimeValue(message.sendAt),
            });
          } else {
            void handleSave(data);
          }
        })}
        actions={[
          {
            text: isUploading ? 'Cancel Upload' : 'Cancel',
            callback: () => handleCancel(),
            disabled: isSending, // Only disable when sending SMS, not when uploading
            ...generateTrackingProps('cancel-button'),
          },
          // Only show Schedule button when creating a new message
          ...(scheduledSmsFF
            ? [
                {
                  text: 'Schedule',
                  callback: () => void handleSchedule(),
                  icon: <ClockIcon width={20} height={20} />,
                  variant: 'outlined' as const,
                  disabled:
                    hasNoPhoneNumbers ||
                    !!templateValidationError ||
                    isSending ||
                    isUploading,
                  ...generateTrackingProps('schedule-button'),
                },
              ]
            : []),
          {
            text: isSending
              ? 'Sending...'
              : isUploading
              ? 'Uploading...'
              : message
              ? 'Update'
              : 'Send',
            type: 'submit',
            isPrimary: true,
            disabled:
              hasNoPhoneNumbers ||
              !!templateValidationError ||
              loadingCreateMessage ||
              loadingUpdateMessage,
            ...generateTrackingProps(message ? 'update-button' : 'send-button'),
          },
        ]}
      >
        <>
          <Box sx={{ marginBottom: 2 }}>
            {user?.phoneNumbers &&
              user.phoneNumbers.find(
                phoneNumber =>
                  !!phoneNumber.doNotContact &&
                  phoneNumber.doNotContact?.reason === '"STOP" message'
              ) && (
                <StyledAlert
                  message={`This Member texted STOP for do not contact. Instruct member to text START to opt back in and receive text messages.`}
                  severity="info"
                  variant="filled"
                  icon={<ClockIcon height={20} width={20} />}
                />
              )}
            {scheduledSmsFF && message?.sendAt && (
              <StyledAlert
                message={`Message scheduled for ${new Date(
                  message.sendAt
                ).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}`}
                severity="info"
                variant="filled"
              />
            )}
          </Box>
          <FormBuilder
            config={formConfig}
            control={control}
            formState={formState}
          />

          {selectedFiles.length > 0 ||
          (message?.fileIds && message.fileIds.length > 0) ? (
            <div className="attachments">
              <h4 className="attachments-title">Attachments</h4>

              {isUploading && (
                <div className="attachments-upload-progress">
                  <div className="attachments-upload-progress-header">
                    <Typography
                      variant="body2"
                      className="attachments-upload-progress-text"
                    >
                      Uploading {selectedFiles.length} file
                      {selectedFiles.length > 1 ? 's' : ''}...
                    </Typography>
                    <Typography
                      variant="body2"
                      className="attachments-upload-progress-percentage"
                    >
                      {Math.round(uploadProgress)}%
                    </Typography>
                  </div>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    className="attachments-upload-progress-bar"
                  />
                </div>
              )}

              <div className="attachments-file-list">
                {/* Show existing files */}
                {message?.fileIds
                  ?.filter(fileId => !filesToRemove.includes(fileId))
                  .map(fileId => {
                    const fileState = filesMap[fileId];
                    if (!fileState?.file) return null;

                    return (
                      <div key={fileId} className="attachments-file-item">
                        <div className="attachments-file-thumbnail">
                          <FilePreviewer filedId={fileId} />
                        </div>
                        <Typography
                          variant="body2"
                          className="attachments-file-name"
                        >
                          {fileState.file.name}
                        </Typography>
                        <Button
                          onClick={() => removeExistingFile(fileId)}
                          disabled={isSending || isUploading}
                          variant="text"
                          color="error"
                          className="attachments-remove-button"
                          {...generateTrackingProps(
                            'remove-existing-attachment-button'
                          )}
                        >
                          Remove
                        </Button>
                      </div>
                    );
                  })}

                {/* Show new files */}
                {selectedFiles.map(file => {
                  return (
                    <div key={file.name} className="attachments-file-item">
                      <div className="attachments-file-thumbnail">
                        <FileThumbnail file={file} />
                      </div>
                      <Typography
                        variant="body2"
                        className="attachments-file-name"
                      >
                        {file.name}
                      </Typography>
                      <Button
                        onClick={() => removeFile(file)}
                        disabled={isSending || isUploading}
                        variant="text"
                        color="error"
                        className="attachments-remove-button"
                        {...generateTrackingProps('remove-attachment-button')}
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}
              </div>

              <div className="attachments-attach-button-container">
                <Button
                  variant="outlined"
                  component="label"
                  disabled={isUploading}
                  className="attachments-attach-button"
                  {...generateTrackingProps('attach-file-button')}
                >
                  Attach File
                  <FileInput
                    files={selectedFiles}
                    onChange={setSelectedFiles}
                    maxCount={4}
                    allowedFileTypes={SUPPORTED_SMS_FILE_TYPES}
                    maxFileSize={SUPPORTED_SMS_FILE_SIZE}
                  />
                </Button>
              </div>
            </div>
          ) : (
            <ButtonContainer>
              <Button
                variant="text"
                component="label"
                disabled={isUploading}
                className="attachments-attach-button-text"
                {...generateTrackingProps('attach-file-button')}
              >
                Attach File
                <FileInput
                  files={selectedFiles}
                  onChange={setSelectedFiles}
                  maxCount={4}
                  allowedFileTypes={SUPPORTED_SMS_FILE_TYPES}
                  maxFileSize={SUPPORTED_SMS_FILE_SIZE}
                />
              </Button>
            </ButtonContainer>
          )}

          <MessageTemplates
            handleApplyTemplate={handleApplyTemplate}
            user={user}
          />

          {error && (
            <Box sx={{ marginTop: 2 }}>
              <StyledAlert
                message={error}
                severity="error"
                variant="filled"
                actions={
                  <Button
                    variant="text"
                    onClick={clearError}
                    className="dismiss-button"
                  >
                    Dismiss
                  </Button>
                }
              />
            </Box>
          )}
        </>
        {templateValidationError && (
          <Box sx={{ marginBottom: 1 }}>
            <StyledAlert
              message={templateValidationError}
              severity="warning"
              variant="filled"
              actions={
                <Button
                  variant="text"
                  onClick={() => setTemplateValidationError(null)}
                  className="dismiss-button"
                >
                  Dismiss
                </Button>
              }
            />
          </Box>
        )}
        <ScheduleMessageDialog
          message={message}
          handleClose={() => {
            setIsScheduleDialogOpen(false);
            setFormDataForScheduling(null);
          }}
          handleScheduleMessage={handleScheduleMessageWrapper}
          isOpen={isScheduleDialogOpen}
        />
      </CompositeDialog>
    </TrackingProvider>
  );
};

export default MessageDialog;
